import React, { useEffect, useState, useCallback } from 'react';

const PROVIDERS = [
  { value: 'CLAUDE', label: 'Claude (Anthropic)' },
  { value: 'OPENAI', label: 'OpenAI' },
];

const MODELS = {
  CLAUDE: [
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (Fast, Low Cost)' },
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (Balanced)' },
  ],
  OPENAI: [
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Fast, Low Cost)' },
    { value: 'gpt-4o', label: 'GPT-4o (Balanced)' },
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini (Latest, Fast)' },
    { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano (Cheapest)' },
  ],
};

const sectionStyle = { background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '1.2rem 1.4rem', marginTop: '1.2rem' };
const labelStyle = { display: 'block', fontWeight: 600, fontSize: '0.85rem', color: '#334155', marginBottom: '0.3rem' };
const inputStyle = { width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' };
const selectStyle = { ...inputStyle, background: '#fff' };

function PmsNlpConfig({ token }) {
  const [form, setForm] = useState({
    nlp_provider: 'CLAUDE',
    nlp_api_key: '',
    nlp_model: 'claude-haiku-4-5-20251001',
    nlp_max_tokens: 500,
    nlp_temperature: 0.3,
    nlp_rate_limit_per_guest: 20,
    nlp_monthly_cost_cap: 50,
  });
  const [maskedKey, setMaskedKey] = useState('');
  const [keyEditing, setKeyEditing] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [usageCount, setUsageCount] = useState(0);
  const [usageCost, setUsageCost] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
  }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/v1/pms/nlp-config', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load');
      setForm({
        nlp_provider: data.nlp_provider || 'CLAUDE',
        nlp_api_key: '',
        nlp_model: data.nlp_model || 'claude-haiku-4-5-20251001',
        nlp_max_tokens: data.nlp_max_tokens || 500,
        nlp_temperature: data.nlp_temperature ?? 0.3,
        nlp_rate_limit_per_guest: data.nlp_rate_limit_per_guest || 20,
        nlp_monthly_cost_cap: data.nlp_monthly_cost_cap || 50,
      });
      setMaskedKey(data.nlp_api_key_masked || '');
      setKeyEditing(false);
      setUsageCount(data.current_month_usage_count || 0);
      setUsageCost(data.current_month_estimated_cost || 0);
    } catch (err) {
      setError(err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const handleProviderChange = (provider) => {
    const defaultModel = MODELS[provider]?.[0]?.value || '';
    setForm(prev => ({ ...prev, nlp_provider: provider, nlp_model: defaultModel }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = { ...form };
      if (!keyEditing || !form.nlp_api_key) {
        payload.nlp_api_key = maskedKey; // Send masked — backend will skip
      }
      const res = await fetch('/api/v1/pms/nlp-config', {
        method: 'POST',
        headers: headers(true),
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to save');
      setMessage('NLP configuration saved successfully');
      setTimeout(() => setMessage(''), 4000);
      load();
    } catch (err) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const payload = {
        nlp_provider: form.nlp_provider,
        nlp_api_key: keyEditing && form.nlp_api_key ? form.nlp_api_key : maskedKey,
        nlp_model: form.nlp_model,
        nlp_max_tokens: form.nlp_max_tokens,
        nlp_temperature: form.nlp_temperature,
        test_text: 'What time is checkout?'
      };
      const res = await fetch('/api/v1/pms/nlp-config/test', {
        method: 'POST',
        headers: headers(true),
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Test failed');
      setTestResult(data);
    } catch (err) {
      setTestResult({ success: false, error: err?.message || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const costPercent = form.nlp_monthly_cost_cap > 0 ? Math.min(100, (usageCost / form.nlp_monthly_cost_cap) * 100) : 0;

  return (
    <div className="page-shell">
      <div style={{ marginBottom: '0.2rem', color: 'var(--theme-text-secondary)', fontSize: '0.82rem' }}>Settings &gt; NLP Config</div>
      <h2 style={{ margin: '0 0 0.3rem', color: 'var(--theme-primary)' }}>NLP Configuration</h2>
      <p style={{ color: 'var(--theme-text-secondary)', margin: '0 0 1rem', fontSize: '0.88rem' }}>
        Configure the NLP provider, API key, and parameters for the guest voice/text assistant.
      </p>

      {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.5rem', fontSize: '0.85rem' }}>{error}</div>}
      {message && <div style={{ background: '#f0fdf4', color: '#166534', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.5rem', fontSize: '0.85rem' }}>{message}</div>}

      {loading ? <div style={{ color: 'var(--theme-text-secondary)' }}>Loading...</div> : (
        <>
          {/* Provider Selection */}
          <div style={sectionStyle}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--theme-primary)', marginBottom: '0.8rem' }}>NLP Provider</div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              {PROVIDERS.map(p => (
                <label key={p.value} style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.7rem 1.2rem',
                  border: `2px solid ${form.nlp_provider === p.value ? 'var(--theme-primary)' : 'var(--theme-border)'}`,
                  borderRadius: 8, cursor: 'pointer', background: form.nlp_provider === p.value ? '#eff6ff' : '#fff',
                  fontWeight: form.nlp_provider === p.value ? 600 : 400, fontSize: '0.9rem', transition: 'all 0.2s'
                }}>
                  <input type="radio" name="nlp_provider" value={p.value}
                    checked={form.nlp_provider === p.value}
                    onChange={() => handleProviderChange(p.value)}
                    style={{ accentColor: 'var(--theme-primary)' }}
                  />
                  {p.label}
                </label>
              ))}
            </div>
          </div>

          {/* API Configuration */}
          <div style={sectionStyle}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--theme-primary)', marginBottom: '0.8rem' }}>API Configuration</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>API Key</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <input
                      type={showKey ? 'text' : 'password'}
                      value={keyEditing ? form.nlp_api_key : maskedKey}
                      placeholder={maskedKey ? `Current key: ${maskedKey}` : 'Enter API key...'}
                      onFocus={() => { if (!keyEditing) { setKeyEditing(true); setForm(prev => ({ ...prev, nlp_api_key: '' })); } }}
                      onChange={(e) => setForm(prev => ({ ...prev, nlp_api_key: e.target.value }))}
                      style={inputStyle}
                    />
                  </div>
                  <button onClick={() => setShowKey(!showKey)}
                    style={{ padding: '0.5rem 0.7rem', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {showKey ? 'Hide' : 'Show'}
                  </button>
                  {keyEditing && (
                    <button onClick={() => { setKeyEditing(false); setForm(prev => ({ ...prev, nlp_api_key: '' })); }}
                      style={{ padding: '0.5rem 0.7rem', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--theme-text-secondary)' }}>
                      Cancel
                    </button>
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--theme-text-muted)', marginTop: '0.3rem' }}>
                  API key is stored encrypted. Only the last 4 characters are displayed after saving.
                </div>
              </div>

              <div>
                <label style={labelStyle}>Model</label>
                <select value={form.nlp_model} onChange={(e) => setForm(prev => ({ ...prev, nlp_model: e.target.value }))} style={selectStyle}>
                  {(MODELS[form.nlp_provider] || []).map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Max Tokens</label>
                <input type="number" min="100" max="2000" step="50"
                  value={form.nlp_max_tokens}
                  onChange={(e) => setForm(prev => ({ ...prev, nlp_max_tokens: parseInt(e.target.value) || 500 }))}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Temperature</label>
                <input type="number" min="0" max="1" step="0.05"
                  value={form.nlp_temperature}
                  onChange={(e) => setForm(prev => ({ ...prev, nlp_temperature: parseFloat(e.target.value) || 0.3 }))}
                  style={inputStyle}
                />
                <div style={{ fontSize: '0.75rem', color: 'var(--theme-text-muted)', marginTop: '0.2rem' }}>
                  Lower = more deterministic (recommended: 0.2-0.4)
                </div>
              </div>
            </div>
          </div>

          {/* Rate Limiting */}
          <div style={sectionStyle}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--theme-primary)', marginBottom: '0.8rem' }}>Rate Limiting</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>Max Requests per Guest (per 15 min)</label>
                <input type="number" min="1" max="100"
                  value={form.nlp_rate_limit_per_guest}
                  onChange={(e) => setForm(prev => ({ ...prev, nlp_rate_limit_per_guest: parseInt(e.target.value) || 20 }))}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Monthly Cost Cap (USD)</label>
                <input type="number" min="1" max="10000" step="5"
                  value={form.nlp_monthly_cost_cap}
                  onChange={(e) => setForm(prev => ({ ...prev, nlp_monthly_cost_cap: parseFloat(e.target.value) || 50 }))}
                  style={inputStyle}
                />
                <div style={{ fontSize: '0.75rem', color: 'var(--theme-text-muted)', marginTop: '0.2rem' }}>
                  NLP will be temporarily disabled when this cap is reached.
                </div>
              </div>
            </div>
          </div>

          {/* Usage Stats */}
          <div style={sectionStyle}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--theme-primary)', marginBottom: '0.8rem' }}>Current Month Usage</div>
            <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>{usageCount}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--theme-text-secondary)' }}>Requests</div>
              </div>
              <div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>${usageCost.toFixed(4)}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--theme-text-secondary)' }}>Estimated Cost</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'var(--theme-text-secondary)', marginBottom: '0.3rem' }}>
                  <span>{costPercent.toFixed(1)}% of cap</span>
                  <span>${form.nlp_monthly_cost_cap}</span>
                </div>
                <div style={{ height: 8, background: 'var(--theme-border)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${costPercent}%`, height: '100%', background: costPercent > 80 ? '#ef4444' : costPercent > 50 ? '#f59e0b' : '#22c55e', borderRadius: 4, transition: 'width 0.3s' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Test Connection */}
          <div style={sectionStyle}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--theme-primary)', marginBottom: '0.8rem' }}>Test Connection</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button onClick={handleTest} disabled={testing}
                style={{ padding: '0.5rem 1.2rem', background: '#475569', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: '0.85rem', cursor: testing ? 'not-allowed' : 'pointer', opacity: testing ? 0.7 : 1 }}>
                {testing ? 'Testing...' : 'Test NLP Connection'}
              </button>
              {testResult && (
                <div style={{ fontSize: '0.85rem' }}>
                  {testResult.success ? (
                    <span style={{ color: 'var(--theme-success)' }}>
                      Connection successful — Model: {testResult.model}, Intent: {testResult.parsed_intent}, Response: {testResult.response_time_ms}ms
                    </span>
                  ) : (
                    <span style={{ color: 'var(--theme-danger)' }}>Failed: {testResult.error}</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Save / Reset */}
          <div style={{ marginTop: '1.2rem', display: 'flex', gap: '0.6rem' }}>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '0.6rem 2rem', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: '0.9rem', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
            <button onClick={load}
              style={{ padding: '0.6rem 1.2rem', background: 'var(--theme-border-light)', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
              Reset
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default PmsNlpConfig;
